<template>
  <v-form ref="form" v-model="valid" lazy-validation>
    <v-row dense>
      <!-- Host -->
      <v-col cols="9">
        <v-text-field
          v-model="kafkaSettings.host"
          :label="$t('labels.host')"
          variant="outlined"
          density="compact"
          :rules="[rules.required, rules.validHost]"
          :disabled="!isOwner"
          required
        ></v-text-field>
      </v-col>
      <!-- Port -->
      <v-col cols="3">
        <v-text-field
          v-model="kafkaSettings.port"
          :label="$t('labels.port')"
          variant="outlined"
          density="compact"
          :rules="[rules.required, rules.numeric]"
          :disabled="!isOwner"
          required
          type="number"
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row dense>
      <!-- Topic Name -->
      <v-col cols="12">
        <v-text-field
          v-model="kafkaSettings.topicName"
          :label="$t('labels.topic-name')"
          variant="outlined"
          density="compact"
          :rules="[rules.required]"
          :disabled="!isOwner"
          required
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row dense>
      <!-- Auth Type -->
      <v-col cols="12">
        <v-select
          v-model="auth.type"
          :items="authTypes"
          :label="$t('labels.auth-type')"
          item-title="text"
          item-value="value"
          variant="outlined"
          density="compact"
          :rules="[rules.required]"
          :disabled="!isOwner"
          required
        ></v-select>
      </v-col>
    </v-row>
    <v-row dense>
      <!-- Username (only show if auth type is SASL) -->
      <v-col cols="6" v-if="auth.type === 'SASL_PLAINTEXT'">
        <v-text-field
          v-model="auth.username"
          :label="$t('labels.username')"
          variant="outlined"
          density="compact"
          :rules="[rules.required]"
          :disabled="!isOwner"
          required
        ></v-text-field>
      </v-col>
      <!-- Password (only show if auth type is SASL) -->
      <v-col cols="6" v-if="auth.type === 'SASL_PLAINTEXT'">
        <v-text-field
          v-model="auth.password"
          :label="$t('labels.password')"
          variant="outlined"
          density="compact"
          type="password"
          :rules="[rules.required]"
          :disabled="!isOwner"
          required
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" class="d-flex justify-end">
        <v-btn
          variant="outlined"
          color="secondary"
          :disabled="!isOwner"
          @click="resetFields"
        >
          {{ $t("labels.reset") }}
        </v-btn>
        <v-btn
          class="ml-4"
          variant="outlined"
          color="green"
          :disabled="!isOwner || !valid"
          @click="testConnection"
        >
          {{ $t("labels.test-connection") }}
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script setup>
import {
  ref,
  defineEmits,
  watch,
  useTemplateRef,
  nextTick,
  defineExpose,
} from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

defineExpose({
  resetForm,
});

const props = defineProps(["bindings", "isOwner"]);

const emit = defineEmits(["dataSourceAssociation", "resetFields"]);

const form = useTemplateRef("form");

const valid = ref(false);
const kafkaSettings = ref({
  type: "kafka",
  host: "",
  port: "",
  topicName: "",
});
const auth = ref({
  type: "PLAINTEXT",
  username: "",
  password: "",
});

const kafkaSettingsDefault = {
  type: "kafka",
  host: "",
  port: "",
  topicName: "",
};
const authDefault = {
  type: "PLAINTEXT",
  username: "",
  password: "",
};

const authTypes = [
  { text: "None", value: "PLAINTEXT" },
  { text: "SALS", value: "SASL_PLAINTEXT" },
  { text: "SSL", value: "SSL", props: { disabled: true } },
];
const validHostRegex = /^(?!https?:\/\/|tcp:\/\/)([a-zA-Z0-9.-]+)(?::\d+)?$/;
const rules = {
  required: (v) => !!v || t("errors.required-field"),
  numeric: (v) => !isNaN(parseFloat(v)) || t("errors.numeric-field"),
  validHost: (v) => validHostRegex.test(v) || t("errors.invalid-host"),
};

watch(
  () => props.bindings,
  (newVal) => {
    if (newVal) {
      let newValClone = { ...newVal };
      if (newValClone.auth) {
        auth.value = newValClone.auth;
        delete newValClone.auth;
      } else {
        auth.value = JSON.parse(JSON.stringify(authDefault));
      }
      kafkaSettings.value = newValClone;
    } else {
      resetForm();
    }
  },
  { immediate: true },
);

function resetForm() {
  kafkaSettings.value = JSON.parse(JSON.stringify(kafkaSettingsDefault));
  auth.value = JSON.parse(JSON.stringify(authDefault));

  nextTick(() => {
    form.value?.resetValidation();
  });
}

function testConnection() {
  let action = {
    ...kafkaSettings.value,
    auth: { ...auth.value },
  };
  emit("dataSourceAssociation", action);
}

function resetFields() {
  emit("resetFields");
}
</script>
